export type LeadStatus =
  | "Initial"
  | "Pending"
  | "Success"
  | "Failure"
  | "DuplicateIP"
  | "DuplicateEmail"
  | "BrandDuplication"
  | "Not_Supported"
  | "Rejected";

export interface Lead {
  id: number;
  name: string;
  email: string;
  phone: string;
  websiteSharedUid: string;
  brandUid: string;
  brand: string;
  status: LeadStatus;
  industry: string;
  loanPurpose: string;
  amount: string;
  revenue: string;
  credit: string;
  timeInBusiness: string;
  zip: string;
  businessName: string;
  userUniqueId: string;
  trafficSource: string;
  adId: string;
  adPlatformClickId: string;
  date: string;
  reportedAt: string | null;
  created: string;
  modified: string;
  // --- Scoring / brand-outcome fields: all sourced from the
  // brand_leads_report/ response (see "Extending POST .../brand_leads_report/
  // Response" spec). Nullable rather than optional — the API always returns
  // the key, null when not yet applicable (e.g. scoring not run yet).
  qualifiedByBrand: boolean | null;
  incomeFromBrand: number | null;
  brandLeadStatus: string | null;
  questionnaireFillTimeSeconds: number | null;
  leadScore: number | null;
  scoreRationale: string | null;
  approvalLikelihood: number | null;
  approvalRationale: string | null;
  scoringStatus: ScoringStatus | null;
  scoringUpdatedAt: string | null;
  ipAddress: string | null;
  duplicateIpLeads: DuplicateIpLead[];
  reportLog: ReportLogEntry[];
  reportedToMultipleBrands: boolean | null;
}

export interface DuplicateIpLead {
  id: number;
  fullName: string;
  brandName: string;
  created: string;
  // Not in duplicate_ip_leads yet — shown once the API starts sending it.
  email: string | null;
}

export type ScoringStatus = "completed" | "processing" | "failed";

export interface ReportLogEntry {
  id: number;
  attempt: number;
  // The API's brand_leads_report_log rows carry no status field, only a raw,
  // brand-specific response_payload — "status" here is a best-effort
  // inference (see inferReportLogStatus), not a value returned by the API.
  status: "success" | "failure" | "logged";
  createdAt: string;
  payload: string;
  brand: string;
}

// The Leads Management screen is currently scoped to this single site. Its
// website_shared_uid differs per environment (staging/production don't share
// a database), but the site_name is stable across environments, so access
// and the shared_uid to query with are both resolved from it at runtime.
export const LEADS_MANAGEMENT_SITE_NAME = "Opti Fund";

export const STATUS_META: Record<
  LeadStatus,
  { label: string; color: string; bg: string }
> = {
  Initial: { label: "Initial", color: "#1565C0", bg: "#E3F2FD" },
  Pending: { label: "Pending", color: "#E65100", bg: "#FFF3E0" },
  Success: { label: "Success", color: "#2E7D32", bg: "#E8F5E9" },
  Failure: { label: "Failure", color: "#C62828", bg: "#FFEBEE" },
  DuplicateIP: { label: "Duplicate IP", color: "#AD1457", bg: "#FCE4EC" },
  DuplicateEmail: { label: "Duplicate Email", color: "#6A1B9A", bg: "#F3E5F5" },
  BrandDuplication: { label: "Brand Duplication", color: "#4527A0", bg: "#EDE7F6" },
  Not_Supported: { label: "Not Supported", color: "#616161", bg: "#F0F0F0" },
  // A deliberate "don't report" call, visually distinct from Failure.
  Rejected: { label: "Rejected", color: "#4A148C", bg: "#F3E5F5" },
};

// "Report to this brand" calls POST /partners/leads/reassign_and_report/.
export const BRAND_MATCH_REPORT_TO_BRAND_ENABLED = true;

export interface StatusTab {
  key: string;
  label: string;
  // Tabs with more than one status (e.g. Internal Duplication) are fetched
  // as one request per status and merged, since report_status is single-valued.
  statuses: LeadStatus[];
}

// Pending/Initial/Not_Supported leads no longer get their own tab — they
// still show up (with their status chip) under the "All" tab.
export const STATUS_TABS: StatusTab[] = [
  { key: "Success", label: STATUS_META.Success.label, statuses: ["Success"] },
  { key: "Failure", label: STATUS_META.Failure.label, statuses: ["Failure"] },
  {
    key: "InternalDuplication",
    label: "Internal Duplication",
    statuses: ["DuplicateIP", "DuplicateEmail"],
  },
  {
    key: "BrandDuplication",
    label: "Brand Duplication",
    statuses: ["BrandDuplication"],
  },
  { key: "Rejected", label: STATUS_META.Rejected.label, statuses: ["Rejected"] },
];

export const ALL_STATUS_TAB = "All";

// Falls back to "All" for anything that isn't a current tab — e.g. a stale
// ?status=Pending bookmark from before the Pending/Initial/Not Supported
// tabs were removed.
export function resolveStatusTab(status: string | null | undefined): string {
  const isKnownTab = STATUS_TABS.some((tab) => tab.key === status);
  return isKnownTab && status ? status : ALL_STATUS_TAB;
}

// DuplicateIP leads are re-reportable just like Pending — the API only
// blocked the automatic report, it didn't reject the lead outright, so the
// user can still choose to report it manually.
export function isReportableStatus(status: LeadStatus): boolean {
  return status === "Pending" || status === "DuplicateIP";
}

// Any status can move to Rejected except the two terminal ones: Success
// (already reported) and Rejected itself.
export function isRejectableStatus(status: LeadStatus): boolean {
  return status !== "Success" && status !== "Rejected";
}

export interface ScoreMeta {
  color: string;
  bg: string;
  band: string;
}

export function getScoreMeta(score: number): ScoreMeta {
  if (score >= 7) return { color: "#2E7D32", bg: "#E8F5E9", band: "Strong" };
  if (score >= 4) return { color: "#EF6C00", bg: "#FFF3E0", band: "Moderate" };
  return { color: "#C62828", bg: "#FFEBEE", band: "Weak" };
}

export function getQualifiedMeta(
  qualified: boolean | null,
): { label: string; color: string; bg: string } {
  if (qualified === true) return { label: "Qualified", color: "#2E7D32", bg: "#E8F5E9" };
  if (qualified === false) return { label: "Not Qualified", color: "#C62828", bg: "#FFEBEE" };
  return { label: "Pending", color: "#9e9e9e", bg: "#F0F0F0" };
}

// No "Unknown"/null-only option — the server's qualified_by_brand param can
// only express true/false/omitted ("null leads excluded from both"), it
// can't filter for "qualification pending" specifically.
export const QUALIFIED_FILTER_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// Maps to the reported_to_multiple_brands request param — true when a lead
// has been reported to more than one brand.
export const MULTI_BRAND_FILTER_OPTIONS = [
  { value: "All", label: "Any" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// Fixed threshold set per the lead_scoring_min request param spec.
export const MIN_SCORE_FILTER_OPTIONS = [
  { value: "0", label: "Any" },
  { value: "4", label: "4.0+" },
  { value: "6", label: "6.0+" },
  { value: "7", label: "7.0+" },
  { value: "8.5", label: "8.5+" },
];

// has_duplicate_ip is a boolean on the server (shared vs unique), not a
// specific IP value — "view leads from this exact IP" now goes through the
// general search param instead (see goToFilteredLeads' search override).
export const IP_FILTER_OPTIONS = [
  { value: "All", label: "Any IP" },
  { value: "Shared", label: "Shared IP only" },
  { value: "Unique", label: "Unique IP only" },
];

export const SCORING_STATUS_META: Record<
  ScoringStatus,
  { label: string; color: string; bg: string }
> = {
  completed: { label: "Completed", color: "#2E7D32", bg: "#E8F5E9" },
  processing: { label: "Processing", color: "#EF6C00", bg: "#FFF3E0" },
  failed: { label: "Failed", color: "#C62828", bg: "#FFEBEE" },
};

export const REPORT_LOG_STATUS_META: Record<
  ReportLogEntry["status"],
  { label: string; color: string; bg: string }
> = {
  success: { label: "Success", color: "#2E7D32", bg: "#E8F5E9" },
  failure: { label: "Failure", color: "#C62828", bg: "#FFEBEE" },
  logged: { label: "Logged", color: "#616161", bg: "#F0F0F0" },
};

/**
 * The brand_leads_report_log rows returned by the API have no explicit
 * status field — response_payload is a raw, brand-specific blob. This infers
 * a status for display: a common `success`/`status` key inside the payload
 * if present, otherwise the most recent attempt inherits the lead's own
 * report_status, otherwise a neutral "logged" fallback. Best-effort by
 * design — may not be accurate for every brand's payload shape.
 */
function inferReportLogStatus(
  payload: any,
  isLastEntry: boolean,
  leadStatus: LeadStatus,
): ReportLogEntry["status"] {
  if (payload && typeof payload === "object") {
    if (typeof payload.success === "boolean") return payload.success ? "success" : "failure";
    if (typeof payload.status === "string") {
      const normalized = payload.status.toLowerCase();
      if (["success", "accepted", "ok", "approved"].includes(normalized)) return "success";
      if (["failed", "failure", "error", "declined", "rejected"].includes(normalized)) return "failure";
    }
  }
  if (isLastEntry) {
    if (leadStatus === "Success") return "success";
    if (leadStatus === "Failure") return "failure";
  }
  return "logged";
}

// Uses UTC getters, not local ones — see the note above formatAcquiredDate/
// formatAcquiredTime in LeadsTable.tsx for why: the date_from/date_to
// filters this data is fetched with are plain calendar days matched in UTC,
// so reading these same timestamps back with local getters can shift the
// displayed day/time by the browser's UTC offset.
export function formatLeadDateTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  let hours = date.getUTCHours();
  const meridiem = hours < 12 ? "a.m." : "p.m.";
  hours = hours % 12 || 12;
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}, ${hours}:${minutes} ${meridiem}`;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
}

function toIsoDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Leads Management defaults to the last 30 days. */
export function getDefaultDateRange(referenceDate: Date = new Date()): {
  from: string;
  to: string;
} {
  return {
    from: toIsoDateString(new Date(referenceDate.getTime() - 30 * 86400000)),
    to: toIsoDateString(referenceDate),
  };
}

export const MAX_DATE_RANGE_DAYS = 365;

/** The latest end date the picker should allow for a given start date, in yyyy-mm-dd form. */
export function getMaxEndDate(start: string): string | undefined {
  if (!start) return undefined;
  const startDate = new Date(`${start}T00:00:00`);
  if (Number.isNaN(startDate.getTime())) return undefined;
  return toIsoDateString(new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 86400000));
}

/** The earliest start date the picker should allow for a given end date, in yyyy-mm-dd form. */
export function getMinStartDate(end: string): string | undefined {
  if (!end) return undefined;
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(endDate.getTime())) return undefined;
  return toIsoDateString(new Date(endDate.getTime() - MAX_DATE_RANGE_DAYS * 86400000));
}

/** True when end is before start, or more than MAX_DATE_RANGE_DAYS after it (both set). */
export function isDateRangeInvalid(start: string, end: string): boolean {
  if (!start || !end) return false;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
  const daysBetween = (endDate.getTime() - startDate.getTime()) / 86400000;
  return daysBetween < 0 || daysBetween > MAX_DATE_RANGE_DAYS;
}

// DRF's DecimalField serializes to a string by default (avoids float rounding)
// rather than the doc's stated "Decimal" — income_from_brand has been observed
// coming back as e.g. "100.00" instead of 100. Coerce defensively so
// formatCurrency/getScoreMeta/arithmetic always get a real number.
function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

/** Maps a single record from POST /partners/brand_leads_report/ into a Lead. */
export function mapApiLeadToLead(record: any): Lead {
  const reportLogRaw: any[] = Array.isArray(record.brand_leads_report_log)
    ? record.brand_leads_report_log
    : [];
  const sortedLog = [...reportLogRaw].sort(
    (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
  );

  return {
    id: record.id,
    name: record.full_name,
    email: record.email,
    phone: record.phone,
    websiteSharedUid: record.website_shared_uid,
    brandUid: record.brand_uid,
    brand: record.brand_name,
    status: record.report_status,
    industry: record.business_industry,
    loanPurpose: record.loan_purpose,
    amount: record.borrow_amount,
    revenue: record.monthly_revenue,
    credit: record.credit_rating,
    timeInBusiness: record.time_in_business,
    zip: record.zip_code,
    businessName: record.business_name,
    userUniqueId: record.user_unique_id,
    trafficSource: record.traffic_source,
    adId: record.adid,
    adPlatformClickId: record.ad_platform_click_id,
    date: record.date_of_acquisition,
    reportedAt: record.reported_at,
    created: record.created,
    modified: record.modified,
    qualifiedByBrand: record.qualified_by_brand,
    reportedToMultipleBrands: record.reported_to_multiple_brands,
    incomeFromBrand: toNumberOrNull(record.income_from_brand),
    brandLeadStatus: record.brand_lead_status,
    questionnaireFillTimeSeconds: record.questionnaire_fill_time,
    leadScore: toNumberOrNull(record.lead_scoring),
    scoreRationale: record.lead_scoring_description,
    approvalLikelihood: toNumberOrNull(record.loan_approval_likelihood),
    approvalRationale: record.loan_approval_likelihood_description,
    scoringStatus: record.lead_scoring_status,
    scoringUpdatedAt: record.lead_scoring_updated_at,
    ipAddress: record.ip_address,
    duplicateIpLeads: (record.duplicate_ip_leads ?? []).map((dup: any) => ({
      id: dup.id,
      fullName: dup.full_name,
      brandName: dup.brand_name,
      created: dup.created,
      email: dup.email ?? null,
    })),
    reportLog: sortedLog.map((entry, index) => ({
      id: entry.id,
      attempt: index + 1,
      status: inferReportLogStatus(
        entry.response_payload,
        index === sortedLog.length - 1,
        record.report_status,
      ),
      createdAt: entry.created,
      payload:
        typeof entry.response_payload === "string"
          ? entry.response_payload
          : JSON.stringify(entry.response_payload, null, 2),
      brand: entry.brand_name,
    })),
  };
}

export interface LeadsAggregations {
  byStatus: Array<{ status: LeadStatus; count: number }>;
  byDay: Array<{ date: string; count: number }>;
  byBrand: Array<{ brandUid: string; brandName: string; count: number }>;
  qualifiedByBrand: { count: number; successLeadsCount: number; percentOfSuccessLeads: number | null };
  incomeFromBrand: { total: number; successLeadsCount: number; avgPerSuccessLead: number | null };
  avgLeadScore: { average: number | null; scoredLeadsCount: number };
  avgFillTimeSeconds: { averageSeconds: number | null; leadsWithValueCount: number };
  leadScoreDistribution: Array<{ range: string; count: number }>;
  incomeByBrand: { total: number; byBrand: Array<{ brandName: string; amount: number }> };
}

export const EMPTY_AGGREGATIONS: LeadsAggregations = {
  byStatus: [],
  byDay: [],
  byBrand: [],
  qualifiedByBrand: { count: 0, successLeadsCount: 0, percentOfSuccessLeads: null },
  incomeFromBrand: { total: 0, successLeadsCount: 0, avgPerSuccessLead: null },
  avgLeadScore: { average: null, scoredLeadsCount: 0 },
  avgFillTimeSeconds: { averageSeconds: null, leadsWithValueCount: 0 },
  leadScoreDistribution: [],
  incomeByBrand: { total: 0, byBrand: [] },
};

/** Maps the `aggregations` object from POST /partners/brand_leads_report/. */
export function mapApiAggregations(aggregations: any): LeadsAggregations {
  return {
    byStatus: (aggregations?.by_status ?? []).map((item: any) => ({
      status: item.report_status,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    byDay: (aggregations?.by_day ?? []).map((item: any) => ({
      date: item.date,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    byBrand: (aggregations?.by_brand ?? []).map((item: any) => ({
      brandUid: item.brand_uid,
      brandName: item.brand_name,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    qualifiedByBrand: {
      count: toNumberOrNull(aggregations?.qualified_by_brand?.count) ?? 0,
      successLeadsCount: toNumberOrNull(aggregations?.qualified_by_brand?.success_leads_count) ?? 0,
      percentOfSuccessLeads: toNumberOrNull(aggregations?.qualified_by_brand?.percent_of_success_leads),
    },
    incomeFromBrand: {
      total: toNumberOrNull(aggregations?.income_from_brand?.total) ?? 0,
      successLeadsCount: toNumberOrNull(aggregations?.income_from_brand?.success_leads_count) ?? 0,
      avgPerSuccessLead: toNumberOrNull(aggregations?.income_from_brand?.avg_per_success_lead),
    },
    avgLeadScore: {
      average: toNumberOrNull(aggregations?.avg_lead_score?.average),
      scoredLeadsCount: toNumberOrNull(aggregations?.avg_lead_score?.scored_leads_count) ?? 0,
    },
    avgFillTimeSeconds: {
      averageSeconds: toNumberOrNull(aggregations?.avg_fill_time_seconds?.average_seconds),
      leadsWithValueCount: toNumberOrNull(aggregations?.avg_fill_time_seconds?.leads_with_value_count) ?? 0,
    },
    leadScoreDistribution: (aggregations?.lead_score_distribution ?? []).map((item: any) => ({
      range: item.range,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    incomeByBrand: {
      total: toNumberOrNull(aggregations?.income_by_brand?.total) ?? 0,
      byBrand: (aggregations?.income_by_brand?.by_brand ?? []).map((item: any) => ({
        brandName: item.brand_name,
        amount: toNumberOrNull(item.amount) ?? 0,
      })),
    },
  };
}

/**
 * Checks whether the logged-in user (from /users/me/, stored as selectUserData)
 * has access to the site with the given site_name. Mirrors the lookup in
 * src/customHooks/useWebsiteSharedUid.ts.
 */
export function userHasWebsiteAccess(
  userData: any,
  siteName: string,
): boolean {
  const websitePermissions = userData?.data?.website_permissions || [];
  return websitePermissions.some((permission: any) => {
    const details: any = Object.values(permission)[0];
    return details?.site_name === siteName;
  });
}

// ─── Brand Match diagnostic ─────────────────────────────────────────────────
// Maps the BQL "Brand Match Diagnostic by reported Lead ID" query response
// (biz_layer_query/run/5673627085176832/). Brands aren't a fixed set — the
// underlying query cross-joins whichever brands are active in
// raw_brand_hierarchy for the site, so the brand list, per-condition
// requirements, and match results are all discovered from the response
// itself rather than hardcoded.

export type BrandMatchResult = "Match" | "No Match" | "Unknown";

export interface BrandMatchCell {
  brandKey: string;
  requirement: string;
  matchResult: BrandMatchResult;
}

export interface BrandMatchConditionRow {
  key: string;
  nice: string;
  leadAnswer: string;
  cells: BrandMatchCell[];
}

export interface BrandMatchBrandSummary {
  key: string;
  name: string;
  assigned: boolean;
  matchedCount: number;
  totalConditions: number;
  eligible: boolean;
}

export interface BrandMatchDiagnostic {
  brands: BrandMatchBrandSummary[];
  rows: BrandMatchConditionRow[];
}

const REQUIREMENT_SUFFIX = "_requirement";
const MATCH_SUFFIX = "_match";

function titleizeConditionName(name: string): string {
  if (!name) return "";
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function normalizeMatchResult(value: unknown): BrandMatchResult {
  if (value === "Match") return "Match";
  if (value === "No Match") return "No Match";
  return "Unknown";
}

// The lead's assigned brand comes through as a display name (e.g. "Lendzi
// Optifund") while the BQL response's brand keys are short (e.g. "Lendzi") —
// matched by substring rather than equality since there's no shared ID.
function isAssignedBrand(assignedBrandName: string, brandKey: string): boolean {
  if (!assignedBrandName || !brandKey) return false;
  return assignedBrandName.toLowerCase().includes(brandKey.toLowerCase());
}

/**
 * Resolves a Brand Match diagnostic brand key (e.g. "Lendzi", short-form,
 * from the BQL query) to the brand_uid the report endpoint needs, via the
 * brand directory built from the leads list (brand_uid -> full display
 * name, e.g. "Lendzi Optifund") — same substring match as isAssignedBrand,
 * since there's no shared ID between the two.
 */
export function findBrandUidForKey(
  brandDirectory: Record<string, string>,
  brandKey: string,
): string | null {
  if (!brandKey) return null;
  const match = Object.entries(brandDirectory).find(([, name]) =>
    isAssignedBrand(name, brandKey),
  );
  return match ? match[0] : null;
}

// time_in_business comes back as a bare bucket code ("0-6", "24+") while the
// brand requirement column it's compared against reads "12+ months" — append
// the unit so the two sides of the row are legible together. Every other
// condition's raw answer already carries its own unit ("$75,000", "$80,000/mo").
function formatLeadAnswer(conditionKey: string, rawAnswer: unknown): string {
  if (rawAnswer === null || rawAnswer === undefined || rawAnswer === "") return "—";
  const value = String(rawAnswer);
  if (conditionKey === "time_in_business" && !/month/i.test(value)) {
    return `${value} months`;
  }
  return value;
}

/** Maps the `data` object from POST .../biz_layer_query/run/5673627085176832/. */
export function mapBrandMatchResponse(
  raw: any,
  assignedBrandName: string,
): BrandMatchDiagnostic {
  const queryOutput: any[] = Array.isArray(raw?.query_output)
    ? raw.query_output
    : [];
  const outputMetadata: Record<string, { order?: string | number }> =
    raw?.output_metadata ?? {};

  const brandKeys = new Set<string>();
  queryOutput.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key.endsWith(REQUIREMENT_SUFFIX)) {
        brandKeys.add(key.slice(0, -REQUIREMENT_SUFFIX.length));
      }
    });
  });

  const orderedBrandKeys = Array.from(brandKeys).sort((a, b) => {
    const orderA = Number(outputMetadata[`${a}${REQUIREMENT_SUFFIX}`]?.order ?? 0);
    const orderB = Number(outputMetadata[`${b}${REQUIREMENT_SUFFIX}`]?.order ?? 0);
    return orderA - orderB;
  });

  const rows: BrandMatchConditionRow[] = queryOutput.map((row) => ({
    key: row.condition_name,
    nice: titleizeConditionName(row.condition_name),
    leadAnswer: formatLeadAnswer(row.condition_name, row.user_answer),
    cells: orderedBrandKeys.map((brandKey) => ({
      brandKey,
      requirement: row[`${brandKey}${REQUIREMENT_SUFFIX}`] ?? "—",
      matchResult: normalizeMatchResult(row[`${brandKey}${MATCH_SUFFIX}`]),
    })),
  }));

  const totalConditions = rows.length;
  const brands: BrandMatchBrandSummary[] = orderedBrandKeys.map((brandKey) => {
    const matchedCount = rows.filter(
      (row) =>
        row.cells.find((cell) => cell.brandKey === brandKey)?.matchResult ===
        "Match",
    ).length;
    return {
      key: brandKey,
      name: brandKey,
      assigned: isAssignedBrand(assignedBrandName, brandKey),
      matchedCount,
      totalConditions,
      eligible: totalConditions > 0 && matchedCount === totalConditions,
    };
  });

  return { brands, rows };
}
